import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import InvoiceList from './components/InvoiceList';
import InvoiceDetail from './components/InvoiceDetail';
import InvoiceForm from './components/InvoiceForm';
import ClientList from './components/ClientList';
import Settings from './components/Settings';
import MyDocuments from './components/MyDocuments';
import ClientReports from './components/ClientReports';
import ClientEmployees from './components/ClientEmployees';
import AdminTasks from './components/AdminTasks';
import SetPassword from './components/SetPassword';
import MyCheques from './components/MyCheques';
import ChequeList from './components/ChequeList';
import StickyNotes from './components/StickyNotes';

interface PrivateRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, adminOnly = false }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
};

const AppRoutes: React.FC = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      <Route 
        path="/login" 
        element={user ? <Navigate to="/" replace /> : <Login />} 
      />
      <Route 
        path="/setup-password" 
        element={<SetPassword />} 
      />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <PrivateRoute>
            <InvoiceList />
          </PrivateRoute>
        }
      />
      <Route
        path="/invoices/new"
        element={
          <PrivateRoute adminOnly>
            <InvoiceForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/invoices/:id"
        element={
          <PrivateRoute>
            <InvoiceDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <PrivateRoute adminOnly>
            <ClientList />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute adminOnly>
            <Settings />
          </PrivateRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <PrivateRoute adminOnly>
            <AdminTasks />
          </PrivateRoute>
        }
      />
      <Route
        path="/my-documents"
        element={
          <PrivateRoute>
            <MyDocuments />
          </PrivateRoute>
        }
      />
      <Route
        path="/my-reports"
        element={
          <PrivateRoute>
            <ClientReports />
          </PrivateRoute>
        }
      />
      <Route
        path="/my-employees"
        element={
          <PrivateRoute>
            <ClientEmployees />
          </PrivateRoute>
        }
      />
      <Route
        path="/cheques"
        element={
          <PrivateRoute adminOnly>
            <ChequeList />
          </PrivateRoute>
        }
      />
      <Route
        path="/my-cheques"
        element={
          <PrivateRoute>
            <MyCheques />
          </PrivateRoute>
        }
      />
      <Route
        path="/my-notes"
        element={
          <PrivateRoute adminOnly>
            <StickyNotes />
          </PrivateRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
